import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('general'); // general, materias, estudiantes, recursos
  const [students, setStudents] = useState([]);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingStudent, setViewingStudent] = useState(null);

  const [subjectFormData, setSubjectFormData] = useState({
    name: '',
    gradeType: 'notas', // notas o conceptos
    hasGeneralAverage: 'si', // si o no
    hasExam: 'no', // si o no
    teacherId: '',
    teacherName: '',
    hours: 4
  });

  useEffect(() => {
    loadCourse();
    loadFormData();
    loadStudents();
  }, [id]);

  const loadCourse = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await courseService.getCourseById(id);
      if (response.success) {
        setCourse(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error al cargar la información del curso');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFormData = async () => {
    const subjectsResponse = await courseService.getAvailableSubjects();
    if (subjectsResponse.success) {
      setAvailableSubjects(subjectsResponse.data);
    }

    const teachersResponse = await courseService.getTeachers();
    if (teachersResponse.success) {
      setTeachers(teachersResponse.data);
    }
  };

  const loadStudents = () => {
    // Mock students asignados al curso - esto debería venir del servicio
    const mockStudents = [
      { id: 1, name: 'Ana Martínez González', rut: '20.123.456-7', email: 'ana.martinez@educapp.cl', status: 'activo' },
      { id: 2, name: 'Carlos Rodríguez Silva', rut: '20.234.567-8', email: 'carlos.rodriguez@educapp.cl', status: 'activo' },
      { id: 3, name: 'María López Torres', rut: '20.345.678-9', email: 'maria.lopez@educapp.cl', status: 'activo' }
    ];
    setStudents(mockStudents);

    // Mock todos los estudiantes disponibles - esto debería venir del servicio de usuarios con role='alumno'
    const allStudents = [
      { id: 1, name: 'Ana Martínez González', rut: '20.123.456-7', email: 'ana.martinez@educapp.cl', status: 'activo' },
      { id: 2, name: 'Carlos Rodríguez Silva', rut: '20.234.567-8', email: 'carlos.rodriguez@educapp.cl', status: 'activo' },
      { id: 3, name: 'María López Torres', rut: '20.345.678-9', email: 'maria.lopez@educapp.cl', status: 'activo' },
      { id: 4, name: 'Juan Pérez Muñoz', rut: '20.456.789-0', email: 'juan.perez@educapp.cl', status: 'activo' },
      { id: 5, name: 'Laura Sánchez Vera', rut: '20.567.890-1', email: 'laura.sanchez@educapp.cl', status: 'activo' },
      { id: 6, name: 'Diego Fernández López', rut: '20.678.901-2', email: 'diego.fernandez@educapp.cl', status: 'activo' },
      { id: 7, name: 'Valentina Castro Ruiz', rut: '20.789.012-3', email: 'valentina.castro@educapp.cl', status: 'activo' },
      { id: 8, name: 'Sebastián Morales Díaz', rut: '20.890.123-4', email: 'sebastian.morales@educapp.cl', status: 'activo' },
      { id: 9, name: 'Camila Rojas Vera', rut: '20.901.234-5', email: 'camila.rojas@educapp.cl', status: 'activo' },
      { id: 10, name: 'Matías Silva Campos', rut: '20.012.345-6', email: 'matias.silva@educapp.cl', status: 'activo' }
    ];
    setAvailableStudents(allStudents);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleAddSubject = () => {
    setEditingSubject(null);
    setSubjectFormData({
      name: '',
      gradeType: 'notas',
      hasGeneralAverage: 'si',
      hasExam: 'no',
      teacherId: '',
      teacherName: '',
      hours: 4
    });
    setShowSubjectForm(true);
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectFormData({
      name: subject.name,
      gradeType: subject.gradeType || 'notas',
      hasGeneralAverage: subject.hasGeneralAverage || 'si',
      hasExam: subject.hasExam || 'no',
      teacherId: subject.teacherId,
      teacherName: subject.teacherName,
      hours: subject.hours
    });
    setShowSubjectForm(true);
  };

  const handleSubjectFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'teacherId') {
      const teacher = teachers.find(t => t.id === parseInt(value));
      setSubjectFormData(prev => ({
        ...prev,
        teacherId: value,
        teacherName: teacher ? teacher.name : ''
      }));
    } else {
      setSubjectFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmitSubject = async (e) => {
    e.preventDefault();

    if (!subjectFormData.name || !subjectFormData.teacherId) {
      showNotification('Debes completar todos los campos', 'error');
      return;
    }

    try {
      let response;
      if (editingSubject) {
        response = await courseService.updateCourseSubject(
          id,
          editingSubject.id,
          subjectFormData
        );
      } else {
        response = await courseService.addSubjectToCourse(id, subjectFormData);
      }

      if (response.success) {
        showNotification(response.message);
        setShowSubjectForm(false);
        setEditingSubject(null);
        loadCourse();
      } else {
        showNotification(response.message, 'error');
      }
    } catch (error) {
      showNotification('Error al guardar materia', 'error');
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    if (!window.confirm(`¿Estás seguro de eliminar la materia "${subjectName}"?`)) {
      return;
    }

    try {
      const response = await courseService.removeSubjectFromCourse(id, subjectId);
      if (response.success) {
        showNotification(response.message);
        loadCourse();
      } else {
        showNotification(response.message, 'error');
      }
    } catch (error) {
      showNotification('Error al eliminar materia', 'error');
    }
  };

  // Student handlers
  const handleAddStudent = () => {
    setSearchTerm('');
    setShowStudentModal(true);
  };

  const handleSelectStudent = (student) => {
    // Verificar si el estudiante ya está en el curso
    if (students.find(s => s.id === student.id)) {
      showNotification('Este alumno ya está asignado al curso', 'error');
      return;
    }

    setStudents([...students, student]);
    showNotification('Alumno agregado al curso exitosamente');
    setShowStudentModal(false);
    setSearchTerm('');
  };

  const handleRemoveStudent = (studentId, studentName) => {
    if (!window.confirm(`¿Estás seguro de quitar a "${studentName}" del curso?`)) {
      return;
    }

    setStudents(students.filter(s => s.id !== studentId));
    showNotification('Alumno removido del curso exitosamente');
  };

  const handleViewStudent = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setViewingStudent(student);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
          <p className="text-gray-600 mt-4">Cargando información del curso...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/courses')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al listado
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Curso no encontrado</h3>
            <p className="text-gray-600">{error || 'No se pudo cargar la información del curso'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
            notification.type === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-green-500 text-white'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/courses')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al listado
        </button>
      </div>

      {/* Course Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{course.name}</h1>
              <p className="text-primary-100">Año {course.year}</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                course.status === 'activo'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {course.status === 'activo' ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary-600">{course.students}</div>
            <div className="text-sm text-gray-600 mt-1">Estudiantes</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{course.subjects?.length || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Materias</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {course.subjects?.reduce((sum, s) => sum + s.hours, 0) || 0}
            </div>
            <div className="text-sm text-gray-600 mt-1">Horas Semanales</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{course.level}</div>
            <div className="text-sm text-gray-600 mt-1">Nivel</div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Tabs Header */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'general'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>General</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('asignaturas')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'asignaturas'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span>Asignaturas</span>
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {course.subjects?.length || 0}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('estudiantes')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'estudiantes'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1l-3 3m0 0l-3-3m3 3V5" />
                </svg>
                <span>Estudiantes</span>
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {course.students}
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('recursos')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'recursos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span>Recursos</span>
              </div>
            </button>
          </nav>
        </div>

        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Información General
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Nombre:</span>
                    <span className="font-medium text-gray-900">{course.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Nivel:</span>
                    <span className="font-medium text-gray-900">{course.level}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Sección:</span>
                    <span className="font-medium text-gray-900">{course.section}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Año:</span>
                    <span className="font-medium text-gray-900">{course.year}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Fecha de Creación:</span>
                    <span className="font-medium text-gray-900">
                      {new Date(course.createdAt).toLocaleDateString('es-CL')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profesor Jefe
                </h2>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{course.headTeacher}</p>
                    <p className="text-sm text-gray-600">Encargado del curso</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Asignaturas Tab */}
        {activeTab === 'asignaturas' && (
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Asignaturas del Curso</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gestiona las asignaturas y su configuración
                </p>
              </div>
              <button
                onClick={handleAddSubject}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Agregar Asignatura
              </button>
            </div>

            {/* Subject Form */}
            {showSubjectForm && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <form onSubmit={handleSubmitSubject} className="space-y-4">
                  <h3 className="font-medium text-gray-900">
                    {editingSubject ? 'Editar Asignatura' : 'Nueva Asignatura'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Asignatura *
                      </label>
                      <select
                        name="name"
                        value={subjectFormData.name}
                        onChange={handleSubjectFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="">Seleccionar</option>
                        {availableSubjects.map(subject => (
                          <option key={subject.id} value={subject.name}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo Calificación *
                      </label>
                      <select
                        name="gradeType"
                        value={subjectFormData.gradeType}
                        onChange={handleSubjectFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="notas">Notas</option>
                        <option value="conceptos">Conceptos (MB)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Promedio General *
                      </label>
                      <select
                        name="hasGeneralAverage"
                        value={subjectFormData.hasGeneralAverage}
                        onChange={handleSubjectFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="si">Sí</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Examen *
                      </label>
                      <select
                        name="hasExam"
                        value={subjectFormData.hasExam}
                        onChange={handleSubjectFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="si">Sí</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Docente *
                      </label>
                      <select
                        name="teacherId"
                        value={subjectFormData.teacherId}
                        onChange={handleSubjectFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="">Seleccionar</option>
                        {teachers.map(teacher => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hrs / Sem *
                      </label>
                      <input
                        type="number"
                        name="hours"
                        value={subjectFormData.hours}
                        onChange={handleSubjectFormChange}
                        min="1"
                        max="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSubjectForm(false);
                        setEditingSubject(null);
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg"
                    >
                      {editingSubject ? 'Actualizar' : 'Agregar'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Subjects Table */}
            <div className="overflow-x-auto">
              {!course.subjects || course.subjects.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay asignaturas</h3>
                  <p className="text-gray-600">Agrega asignaturas al curso para empezar</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Asignatura
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo Calificación
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Promedio General
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Examen
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Docente
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hrs / Sem
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {course.subjects.map(subject => (
                      <tr key={subject.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{subject.name}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            (subject.gradeType || 'notas') === 'notas'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {(subject.gradeType || 'notas') === 'notas' ? 'Notas' : 'Conceptos (MB)'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            (subject.hasGeneralAverage || 'si') === 'si'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(subject.hasGeneralAverage || 'si') === 'si' ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            (subject.hasExam || 'no') === 'si'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(subject.hasExam || 'no') === 'si' ? 'Sí' : 'No'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{subject.teacherName}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-medium text-gray-900">{subject.hours}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditSubject(subject)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Editar"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteSubject(subject.id, subject.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Eliminar"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Estudiantes Tab */}
        {activeTab === 'estudiantes' && (
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Estudiantes del Curso</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Listado de estudiantes matriculados en {course.name}
                </p>
              </div>
              <button
                onClick={handleAddStudent}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Agregar Alumno
              </button>
            </div>

            {/* Student Modal */}
            {showStudentModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Agregar Alumno al Curso</h3>
                      <button
                        onClick={() => setShowStudentModal(false)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Search input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Buscar por nombre, RUT o email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                      <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Students list */}
                  <div className="overflow-y-auto max-h-96 p-6">
                    {availableStudents
                      .filter(student => {
                        const search = searchTerm.toLowerCase();
                        return (
                          student.name.toLowerCase().includes(search) ||
                          student.rut.includes(search) ||
                          student.email.toLowerCase().includes(search)
                        );
                      })
                      .map((student) => {
                        const isAssigned = students.find(s => s.id === student.id);
                        return (
                          <div
                            key={student.id}
                            className={`flex items-center justify-between p-4 border rounded-lg mb-3 ${
                              isAssigned
                                ? 'bg-gray-50 border-gray-300 opacity-60'
                                : 'bg-white border-gray-200 hover:bg-primary-50 hover:border-primary-300'
                            }`}
                          >
                            <div className="flex items-center flex-1">
                              <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-primary-600 font-medium text-sm">
                                  {student.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{student.name}</div>
                                <div className="text-xs text-gray-500">{student.rut} • {student.email}</div>
                              </div>
                            </div>
                            {isAssigned ? (
                              <span className="text-xs text-gray-500 font-medium">Ya asignado</span>
                            ) : (
                              <button
                                onClick={() => handleSelectStudent(student)}
                                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                              >
                                Agregar
                              </button>
                            )}
                          </div>
                        );
                      })}

                    {availableStudents.filter(student => {
                      const search = searchTerm.toLowerCase();
                      return (
                        student.name.toLowerCase().includes(search) ||
                        student.rut.includes(search) ||
                        student.email.toLowerCase().includes(search)
                      );
                    }).length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1l-3 3m0 0l-3-3m3 3V5" />
                        </svg>
                        <p>No se encontraron alumnos</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Student Detail Modal */}
            {viewingStudent && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-900">Detalle del Estudiante</h3>
                      <button
                        onClick={() => setViewingStudent(null)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                    {/* Header con avatar */}
                    <div className="flex items-center mb-6 pb-6 border-b border-gray-200">
                      <div className="flex-shrink-0 h-20 w-20 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-bold text-2xl">
                          {viewingStudent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </span>
                      </div>
                      <div className="ml-6">
                        <h2 className="text-2xl font-bold text-gray-900">{viewingStudent.name}</h2>
                        <p className="text-gray-600">{viewingStudent.email}</p>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                          viewingStudent.status === 'activo'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {viewingStudent.status === 'activo' ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>

                    {/* Información Personal */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Información Personal
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">RUT</label>
                          <p className="text-sm font-medium text-gray-900">{viewingStudent.rut}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Email</label>
                          <p className="text-sm font-medium text-gray-900">{viewingStudent.email}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Estado</label>
                          <p className="text-sm font-medium text-gray-900">{viewingStudent.status === 'activo' ? 'Activo' : 'Inactivo'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Curso</label>
                          <p className="text-sm font-medium text-gray-900">{course.name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Información Académica */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Información Académica
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-blue-900">6.2</div>
                          <div className="text-sm text-blue-700 mt-1">Promedio General</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-green-900">92%</div>
                          <div className="text-sm text-green-700 mt-1">Asistencia</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-purple-900">8</div>
                          <div className="text-sm text-purple-700 mt-1">Asignaturas</div>
                        </div>
                      </div>
                    </div>

                    {/* Datos de Contacto */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Apoderado
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Nombre</label>
                            <p className="text-sm font-medium text-gray-900">María González Pérez</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Teléfono</label>
                            <p className="text-sm font-medium text-gray-900">+56 9 1234 5678</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Email</label>
                            <p className="text-sm font-medium text-gray-900">maria.gonzalez@gmail.com</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Parentesco</label>
                            <p className="text-sm font-medium text-gray-900">Madre</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botón de cerrar */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setViewingStudent(null)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Students table */}
            <div className="overflow-x-auto">
              {students.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1l-3 3m0 0l-3-3m3 3V5" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay estudiantes</h3>
                  <p className="text-gray-600">Agrega estudiantes al curso para empezar</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        N°
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estudiante
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        RUT
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((student, index) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {student.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {student.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.rut}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            student.status === 'activo'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {student.status === 'activo' ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleViewStudent(student.id)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Ver detalle"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveStudent(student.id, student.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Quitar del curso"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Recursos Tab */}
        {activeTab === 'recursos' && (
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recursos del Curso</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Documentos y archivos compartidos con el curso
                </p>
              </div>
              <button className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Subir Archivo
              </button>
            </div>

            {/* Mock resources - esto debería venir del servicio */}
            <div className="space-y-2">
              {[
                { name: 'Programa de Estudios 2024.pdf', size: '2.4 MB', date: '15/03/2024', type: 'pdf' },
                { name: 'Material de Apoyo - Matemáticas.docx', size: '1.2 MB', date: '10/03/2024', type: 'doc' },
                { name: 'Calendario de Evaluaciones.xlsx', size: '856 KB', date: '05/03/2024', type: 'xls' },
              ].map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      file.type === 'pdf' ? 'bg-red-100' :
                      file.type === 'doc' ? 'bg-blue-100' :
                      'bg-green-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        file.type === 'pdf' ? 'text-red-600' :
                        file.type === 'doc' ? 'text-blue-600' :
                        'text-green-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{file.size} • {file.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-600 hover:text-primary-600 rounded-lg hover:bg-primary-50" title="Descargar">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button className="p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50" title="Eliminar">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {/* Empty state */}
              {false && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay recursos</h3>
                  <p className="text-gray-600">Sube archivos para compartir con el curso</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;